import { useState, useEffect, useRef } from 'react';
import { worksheetService } from '../../services/api';
import { formatMoney } from '../../utils/pricing';
import './QuoteWorksheet.css';

const SECTION_OPTIONS = [{ value: '', label: '—' }, { value: 'bring', label: 'Bring' }, { value: 'buy', label: 'Buy' }, { value: 'hire', label: 'Hire' }];
const UNIT_HINTS = ['ea', 'hr', 'day', 'km', 'L', 'kg', 'tin', 'box'];
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
// "-$227.46" rather than "$-227.46" for a loss
const money = (v) => (Number(v) < 0 ? `-${formatMoney(Math.abs(Number(v)))}` : formatMoney(v));
const fmtQty = (n) => { const v = Number(n) || 0; return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, ''); };
const FIELDS = ['category_id', 'description', 'qty', 'unit', 'unit_cost', 'supplier', 'list_section'];

function draftOf(l) {
  return {
    category_id: l.category_id ? String(l.category_id) : '', description: l.description, qty: fmtQty(l.qty), unit: l.unit || '',
    unit_cost: String(l.unit_cost), supplier: l.supplier || '', list_section: l.list_section || ''
  };
}

/**
 * One cost line, edited in place: text and number fields save when you leave
 * them, selects save when you change them (like the job's cost lines — no
 * separate Save). The draft re-syncs from the server unless you are still
 * typing in the row.
 */
function Row({ line, categories, onSave, onRemove }) {
  const [d, setD] = useState(() => draftOf(line));
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.contains(document.activeElement)) return; // do not stomp on typing
    setD(draftOf(line));
  }, [line]);
  const set = (patch) => setD((p) => ({ ...p, ...patch }));
  const save = (patch = null) => {
    const next = patch ? { ...d, ...patch } : d;
    if (patch) setD(next);
    const saved = draftOf(line);
    if (FIELDS.every((k) => String(next[k] ?? '') === String(saved[k] ?? ''))) return;
    if (!next.description.trim()) { setD(saved); return; } // never save a blank description
    onSave(line.id, {
      category_id: next.category_id ? Number(next.category_id) : null,
      description: next.description,
      qty: next.qty === '' ? 1 : Number(next.qty),
      unit: next.unit,
      unit_cost: next.unit_cost === '' ? 0 : Number(next.unit_cost),
      supplier: next.supplier,
      list_section: next.list_section || null
    });
  };
  const amount = round2((Number(d.qty) || 0) * (Number(d.unit_cost) || 0));
  return (
    <div className="qw-line" ref={ref}>
      <select aria-label="Category" value={d.category_id} onChange={(e) => save({ category_id: e.target.value })} className="qw-cat">
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        {!categories.some((c) => String(c.id) === String(d.category_id)) && d.category_id && <option value={d.category_id}>{line.category_name || 'Other'}</option>}
      </select>
      <input aria-label="Description" className="qw-desc" title={d.description} value={d.description} onChange={(e) => set({ description: e.target.value })} onBlur={() => save()} />
      <input aria-label="Qty" className="qw-num" type="number" min="0" step="any" value={d.qty} onChange={(e) => set({ qty: e.target.value })} onBlur={() => save()} />
      <input aria-label="Unit" className="qw-unit" list="qw-units" value={d.unit} onChange={(e) => set({ unit: e.target.value })} onBlur={() => save()} />
      <input aria-label="Unit cost" className="qw-num" type="number" min="0" step="any" value={d.unit_cost} onChange={(e) => set({ unit_cost: e.target.value })} onBlur={() => save()} />
      <span className="qw-amount">{formatMoney(amount)}</span>
      <input aria-label="Supplier" className="qw-supplier" value={d.supplier} onChange={(e) => set({ supplier: e.target.value })} onBlur={() => save()} placeholder="Supplier" />
      <select aria-label="On the materials list" className="qw-list" value={d.list_section} onChange={(e) => save({ list_section: e.target.value })} title="Show this on the materials list as Bring, Buy or Hire">
        {SECTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button type="button" className="qw-remove" aria-label="Remove line" onClick={() => onRemove(line.id)}>×</button>
    </div>
  );
}

/**
 * Internal costings behind a saved quote (backlog #773): what the job should
 * cost, so profit and margin are visible before it is sent. Never printed,
 * emailed or shown on the client's quote page. `onChange` fires after every
 * save so the page around it can refresh.
 */
export default function QuoteWorksheet({ quoteId, onChange = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // null | the add-cost fields
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  async function load() {
    const r = await worksheetService.get(quoteId);
    if (r.success) { setData(r); setNotes(r.costing_notes || ''); setError(''); } else setError(r.error || 'Could not load the costings');
  }
  useEffect(() => { setData(null); setForm(null); load(); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <p className="text-muted">{error || 'Loading…'}</p>;

  const cats = data.categories;
  const catOf = (id) => cats.find((c) => String(c.id) === String(id));
  const lines = data.lines.filter((l) => l.category_id);
  const listOnly = data.lines.length - lines.length;
  const s = data.summary;

  async function saveLine(id, patch) {
    setError('');
    const r = await worksheetService.updateLine(id, patch);
    if (r.success) {
      setData((p) => ({ ...p, lines: p.lines.map((l) => (l.id === r.line.id ? r.line : l)), summary: r.summary }));
      onChange?.();
    } else { setError(r.error || 'Could not save that line'); load(); }
  }
  async function removeLine(id) {
    setError('');
    const r = await worksheetService.deleteLine(id);
    if (r.success) { setData((p) => ({ ...p, lines: p.lines.filter((l) => l.id !== id), summary: r.summary })); onChange?.(); } else setError(r.error || 'Could not remove that line');
  }
  async function saveNotes() {
    if (notes === (data.costing_notes || '')) return;
    const r = await worksheetService.saveNotes(quoteId, { costing_notes: notes });
    if (r.success) setData((p) => ({ ...p, costing_notes: r.costing_notes })); else setError(r.error || 'Could not save the notes');
  }

  function startAdd() {
    setError('');
    setForm({ category_id: cats[0] ? String(cats[0].id) : '', description: '', qty: '1', unit_cost: '', unit: '', supplier: '', staff_id: '', equipment_id: '' });
  }
  const f = form;
  const fcat = f ? catOf(f.category_id) : null;
  const wantsStaff = fcat?.kind === 'labour';
  const wantsEquip = fcat?.kind === 'equipment' && !/^equipment hire$/i.test(fcat.name);
  async function submitAdd(e) {
    e?.preventDefault();
    if (!f.category_id) { setError('Choose a category'); return; }
    const staff = data.staff.find((p) => String(p.id) === f.staff_id);
    const kit = data.equipment.find((k) => String(k.id) === f.equipment_id);
    const description = f.description.trim() || (wantsStaff && staff ? `Labour — ${staff.name}` : wantsEquip && kit ? kit.name : '');
    if (!description) { setError('Describe the cost'); return; }
    setBusy(true);
    const body = { category_id: Number(f.category_id), description, qty: f.qty === '' ? 1 : Number(f.qty), supplier: f.supplier };
    if (f.unit) body.unit = f.unit;
    if (f.unit_cost !== '') body.unit_cost = Number(f.unit_cost);
    if (wantsStaff && f.staff_id) body.staff_id = Number(f.staff_id);
    if (wantsEquip && f.equipment_id) body.equipment_id = Number(f.equipment_id);
    const r = await worksheetService.addLine(quoteId, body);
    setBusy(false);
    if (r.success) { setData((p) => ({ ...p, lines: [...p.lines, r.line], summary: r.summary })); setForm(null); onChange?.(); } else setError(r.error || 'Could not add the cost');
  }

  const profitClass = s.profit < 0 ? 'qw-neg' : 'qw-pos';
  return (
    <div className="qw">
      <p className="qw-banner" role="note"><strong>Internal</strong> — never on the PDF, the email or the client's quote page.</p>

      <div className="qw-tiles">
        <div className="qw-tile"><span>Quote price</span><strong>{formatMoney(s.price)}</strong></div>
        <div className="qw-tile"><span>Costs</span><strong>{formatMoney(s.costs)}</strong><em>{s.cost_line_count} line{s.cost_line_count === 1 ? '' : 's'}{s.labour_hours ? ` · ${fmtQty(s.labour_hours)} h labour` : ''}</em></div>
        <div className="qw-tile"><span>Overheads</span><strong>{s.overheads === null ? '—' : formatMoney(s.overheads)}</strong>{s.overhead_working && <em>{s.overhead_working}</em>}</div>
        <div className={`qw-tile ${profitClass}`}><span>Profit</span><strong>{money(s.profit)}</strong></div>
        <div className={`qw-tile ${profitClass}`}><span>Margin</span><strong>{s.margin_pct === null ? '—' : `${s.margin_pct}%`}</strong></div>
      </div>

      <div className="qw-head">
        <h3>Costs</h3>
        <button type="button" className="btn btn-sm btn-primary" onClick={startAdd} disabled={!!form}>+ Add cost</button>
      </div>

      {f && (
        <form className="qw-add" onSubmit={submitAdd}>
          <label>Category
            <select value={f.category_id} onChange={(e) => setForm({ ...f, category_id: e.target.value, staff_id: '', equipment_id: '' })}>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {wantsStaff && (
            <label>Whose hours
              <select value={f.staff_id} onChange={(e) => setForm({ ...f, staff_id: e.target.value })}>
                <option value="">Choose…</option>
                {data.staff.map((p) => <option key={p.id} value={p.id}>{p.name}{p.hourly_cost ? ` · ${formatMoney(p.hourly_cost)}/h` : ''}</option>)}
              </select>
            </label>
          )}
          {wantsEquip && (
            <label>Our gear
              <select value={f.equipment_id} onChange={(e) => setForm({ ...f, equipment_id: e.target.value })}>
                <option value="">Choose…</option>
                {data.equipment.filter((k) => k.hourly_rate !== null).map((k) => <option key={k.id} value={k.id}>{k.name} · {formatMoney(k.hourly_rate)}/h</option>)}
              </select>
            </label>
          )}
          <label className="qw-add-desc">Description <input value={f.description} onChange={(e) => setForm({ ...f, description: e.target.value })} placeholder="e.g. Priming paint — Dulux 1Step" autoFocus /></label>
          <label>Qty <input type="number" min="0" step="any" value={f.qty} onChange={(e) => setForm({ ...f, qty: e.target.value })} /></label>
          <label>Unit cost <input type="number" min="0" step="any" value={f.unit_cost} onChange={(e) => setForm({ ...f, unit_cost: e.target.value })} placeholder={wantsStaff || wantsEquip ? 'from rate' : '0.00'} /></label>
          <label>Supplier <input value={f.supplier} onChange={(e) => setForm({ ...f, supplier: e.target.value })} placeholder="e.g. Bunnings Helensvale" /></label>
          <div className="qw-add-actions">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setForm(null)}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add'}</button>
          </div>
        </form>
      )}

      {lines.length === 0 && !f ? (
        <p className="text-muted qw-empty">Nothing costed yet. Add what the job will cost — materials, hire, labour — or use "Move to costings" on a line of the quote.</p>
      ) : lines.length > 0 && (
        <div className="qw-table">
          <div className="qw-line qw-line-head">
            <span>Category</span><span>Description</span><span>Qty</span><span>Unit</span><span>Unit cost</span><span>Amount</span><span>Supplier</span><span>On list</span><span />
          </div>
          {lines.map((l) => <Row key={l.id} line={l} categories={cats} onSave={saveLine} onRemove={removeLine} />)}
        </div>
      )}
      <datalist id="qw-units">{UNIT_HINTS.map((u) => <option key={u} value={u} />)}</datalist>
      {listOnly > 0 && <p className="text-muted qw-hint">{listOnly} list-only item{listOnly === 1 ? '' : 's'} on the Materials list {listOnly === 1 ? 'is' : 'are'} not costs, so {listOnly === 1 ? 'it is' : 'they are'} not shown here.</p>}
      {error && <p className="form-error">{error}</p>}

      <div className="form-group qw-notes">
        <label htmlFor="qw_notes">Costing notes</label>
        <textarea id="qw_notes" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Anything to remember about the numbers — saved when you click away" />
      </div>
    </div>
  );
}
